/*
 * Rolig landing — pack selection state, add to cart, gallery, mobile purchase bar,
 * same-day dispatch cut-off and cart drawer suggestions.
 *
 * Every <pude-offer> and <pude-bar> for the same product shares one selected variant, so the
 * hero offer, the final offer and the mobile bar always show the same pack and total.
 * Add to cart mirrors Horizon's product form: dispatch CartLinesUpdateEvent before the request,
 * resolve it with the fresh cart so the drawer re-renders and the header bubble updates.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const PACK_EVENT = 'pude:pack-change';
const BUSY_EVENT = 'pude:busy-change';
const OFFER_EVENT = 'pude:offer-connected';
const selection = new Map();
let busy = false;

/* global Theme */
/** Horizon declares `const Theme` in a classic script: a global binding, not a window property. */
const routes = () => (typeof Theme !== 'undefined' && Theme?.routes) || {};
const cartAddUrl = () => routes().cart_add_url || '/cart/add.js';
const cartUpdateUrl = () => (routes().cart_update_url ? `${routes().cart_update_url}.js` : '/cart/update.js');
const cartUrl = () => routes().cart_url || '/cart';
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const getSelected = (productId) => selection.get(String(productId));

function setSelected(productId, variantId, source, userInitiated = false) {
  const key = String(productId);
  const value = String(variantId);
  if (selection.get(key) === value) return;
  selection.set(key, value);
  document.dispatchEvent(
    new CustomEvent(PACK_EVENT, { detail: { productId: key, variantId: value, source, userInitiated } })
  );
}

function setBusy(value) {
  busy = value;
  document.dispatchEvent(new CustomEvent(BUSY_EVENT, { detail: { busy } }));
}

/** Pack data rendered in Liquid on each pack card (title, formatted total, availability). */
function packData(productId, variantId) {
  const card = document.querySelector(
    `pude-offer[data-product-id="${productId}"] [data-pude-pack][data-variant-id="${variantId}"]`
  );
  if (!card) return null;
  return {
    title: card.dataset.title,
    price: card.dataset.price,
    cta: card.dataset.cta,
    available: card.dataset.available === 'true',
  };
}

/** Shows a pack chosen with ?variant= (a homepage ad link) on the first offer that has its card. */
function variantFromUrl() {
  try {
    return new URL(window.location.href).searchParams.get('variant');
  } catch {
    return null;
  }
}

function translations() {
  const node = document.getElementById('pude-strings');
  if (!node) return {};
  try {
    return JSON.parse(node.textContent);
  } catch {
    return {};
  }
}

const strings = translations();

function clearStatus(status) {
  if (!status) return;
  status.textContent = '';
  delete status.dataset.state;
}

/* ---------- Cart ---------- */

const cartSectionIds = () =>
  [...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId);

async function fetchCart() {
  const cartItems = document.querySelector('cart-items-component');
  if (cartItems) {
    await customElements.whenDefined('cart-items-component');
    if (typeof cartItems.fetchCartData === 'function') return cartItems.fetchCartData();
  }
  const response = await fetch(`${cartUrl()}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
  return response.json();
}

/** Best-effort cart refresh: a failed refresh must never turn a successful add into an error. */
async function fetchCartSafely() {
  try {
    return await fetchCart();
  } catch (error) {
    console.warn('[pude] cart refresh failed', error);
    return null;
  }
}

function resolveCartEvent(deferred, cart, detail) {
  if (cart) {
    deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, ...detail } });
  } else {
    deferred.resolve({ detail });
  }
}

async function openCartDrawer() {
  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (!drawer) {
    window.location.href = cartUrl();
    return;
  }
  await customElements.whenDefined('theme-drawer');
  if (!drawer.isOpen) drawer.open();
}

/**
 * Adds one pack (one bundle variant) to the cart.
 * @param {HTMLElement} source - connected element that dispatches the cart events
 * @param {string} variantId
 * @returns {Promise<{ok: boolean, message?: string}>}
 */
async function addPackToCart(source, variantId) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  let response;
  let data;
  try {
    response = await fetch(cartAddUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: cartSectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    });
    data = await response.json();
  } catch (error) {
    deferred.reject(error);
    source.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Network error', code: 'SERVICE_UNAVAILABLE' }));
    return { ok: false, message: strings.error };
  }

  // The outcome comes from /cart/add.js alone. The cart refresh runs in parallel: on success the
  // drawer opens from add.js's sections at once, and the bubble is reconciled when the refresh lands.
  const cartPromise = fetchCartSafely();
  const detail = { itemCount: 1, source: 'pude-offer', sourceId: source.id };

  if (!response.ok || data.status) {
    const message = data.description || data.message || strings.error;
    const cart = await cartPromise;
    source.dispatchEvent(
      new CartErrorEvent({ error: message, code: 'INVALID', detail: { description: data.description, errors: data.errors } })
    );
    resolveCartEvent(deferred, cart, { ...detail, didError: true });
    return { ok: false, message };
  }

  const icon = document.querySelector('cart-icon');
  const provisional = (Number(icon?.currentCartCount) || 0) + 1;
  resolveCartEvent(deferred, null, { ...detail, itemCount: provisional, sections: data.sections, didError: false });
  cartPromise.then((cart) => {
    if (cart && icon && typeof icon.renderCartBubble === 'function' && cart.item_count !== provisional) {
      icon.renderCartBubble(cart.item_count, false);
    }
  });
  await openCartDrawer();
  return { ok: true };
}

/** Shared add flow with double-submit protection and visible feedback on the triggering control. */
async function runAdd(source, variantId, button, status, onError) {
  if (busy || !variantId) return;
  const pack = packData(source.dataset.productId, variantId);
  if (pack && !pack.available) return;

  setBusy(true);
  const label = button?.querySelector('[data-pude-label]');
  const idleLabel = label?.textContent;
  if (button) button.setAttribute('aria-busy', 'true');
  if (label && strings.adding) label.textContent = strings.adding;
  clearStatus(status);

  const result = await addPackToCart(source, variantId);

  if (button) button.removeAttribute('aria-busy');
  // The pack may have changed during the request: name the current one, not the one saved at submit.
  const current = packData(source.dataset.productId, getSelected(source.dataset.productId));
  if (label) label.textContent = current?.available ? current.cta || idleLabel : idleLabel;
  if (status) {
    if (result.ok) {
      status.textContent = strings.added || '';
      window.setTimeout(() => {
        if (status.textContent === strings.added) status.textContent = '';
      }, 5000);
    } else {
      status.dataset.state = 'error';
      status.textContent = result.message || strings.error || '';
      onError?.();
    }
  }
  setBusy(false);
}

/* ---------- <pude-offer> ---------- */

class PudeOffer extends HTMLElement {
  #abort;

  connectedCallback() {
    this.productId = this.dataset.productId;
    this.form = this.querySelector('form');
    this.button = this.querySelector('[data-pude-submit]');
    this.status = this.querySelector('[data-pude-status]');
    if (!this.form || !this.button) return;

    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.form.addEventListener('change', this.#onChange, { signal });
    this.form.addEventListener('submit', this.#onSubmit, { signal });
    document.addEventListener(PACK_EVENT, this.#onPackChange, { signal });
    document.addEventListener(BUSY_EVENT, this.#onBusy, { signal });

    const current = getSelected(this.productId);
    if (current) {
      this.#apply(current);
    } else {
      // Product pages render ?variant= server side; on the homepage the link's pack is applied here.
      const wanted = variantFromUrl();
      const linked = wanted && this.form.querySelector(`input[name="id"][value="${CSS.escape(wanted)}"]:not(:disabled)`);
      if (linked) {
        const card = linked.closest('[data-pude-pack]');
        if (card) card.hidden = false;
        linked.checked = true;
      }
      const checked = this.#checked();
      if (checked) setSelected(this.productId, checked.value, this);
      this.#render();
    }
    document.dispatchEvent(new CustomEvent(OFFER_EVENT, { detail: { productId: this.productId } }));
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #checked() {
    return this.form.querySelector('input[name="id"]:checked');
  }

  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.name !== 'id') return;
    setSelected(this.productId, input.value, this, true);
  };

  #onPackChange = (event) => {
    if (event.detail.productId !== this.productId) return;
    clearStatus(this.status);
    this.#apply(event.detail.variantId);
    // Only the offer that owns ?variant= (product template hero) rewrites the URL, and only for user choices.
    // An existing ?variant= (homepage ad link) follows the visitor's choice too, so a reload shows the same pack.
    if (event.detail.userInitiated) {
      const url = new URL(window.location.href);
      if (this.hasAttribute('data-update-url') || url.searchParams.has('variant')) {
        url.searchParams.set('variant', event.detail.variantId);
        window.history.replaceState(window.history.state, '', url);
      }
    }
  };

  #onBusy = (event) => {
    this.button.toggleAttribute('data-locked', event.detail.busy);
  };

  #apply(variantId) {
    const input = this.form.querySelector(`input[name="id"][value="${variantId}"]`);
    if (input && !input.disabled) {
      const card = input.closest('[data-pude-pack]');
      if (card) card.hidden = false;
      input.checked = true;
    }
    this.#render();
  }

  #render() {
    const input = this.#checked();
    const priceNode = this.button.querySelector('[data-pude-price]');
    const label = this.button.querySelector('[data-pude-label]');
    if (!input || input.disabled) {
      this.button.setAttribute('aria-disabled', 'true');
      if (label && strings.sold_out) label.textContent = strings.sold_out;
      if (priceNode) priceNode.textContent = '';
      return;
    }
    const card = input.closest('[data-pude-pack]');
    this.button.removeAttribute('aria-disabled');
    // "Læg 2 puder i kurven": the label names the chosen quantity, the price node its total.
    const text = card?.dataset.cta || strings.add_to_cart;
    if (label && text && !this.button.hasAttribute('aria-busy')) label.textContent = text;
    if (priceNode && card) priceNode.textContent = card.dataset.price;
  }

  #onSubmit = (event) => {
    event.preventDefault();
    if (this.button.getAttribute('aria-disabled') === 'true') return;
    const input = this.#checked();
    if (!input || input.disabled) return;
    runAdd(this, input.value, this.button, this.status);
  };
}

/* ---------- <pude-gallery> ---------- */

class PudeGallery extends HTMLElement {
  #abort;

  connectedCallback() {
    this.track = this.querySelector('[data-pude-track]');
    this.slides = [...this.querySelectorAll('[data-pude-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-pude-thumb]')];
    this.prev = this.querySelector('[data-pude-prev]');
    this.next = this.querySelector('[data-pude-next]');
    this.live = this.querySelector('[data-pude-counter]');
    if (!this.track || this.slides.length < 2) return;

    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.index = 0;
    this.observer?.disconnect();
    this.observer = new IntersectionObserver(this.#onIntersect, { root: this.track, threshold: 0.6 });
    this.slides.forEach((slide) => this.observer.observe(slide));
    this.thumbs.forEach((thumb) => thumb.addEventListener('click', this.#onThumb, { signal }));
    this.prev?.addEventListener('click', () => this.go(this.index - 1, true), { signal });
    this.next?.addEventListener('click', () => this.go(this.index + 1, true), { signal });
    this.track.addEventListener('keydown', this.#onKey, { signal });
    this.#setIndex(0);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.observer?.disconnect();
  }

  /** @param {number} index @param {boolean} [announce] - announce the new image (user-triggered moves) */
  go(index, announce = false) {
    const target = Math.max(0, Math.min(this.slides.length - 1, index));
    const slide = this.slides[target];
    if (!slide) return;
    this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: reducedMotion() ? 'auto' : 'smooth' });
    this.#setIndex(target);
    if (announce && this.live) this.live.textContent = slide.getAttribute('aria-label') || '';
  }

  #setIndex(index) {
    this.index = index;
    this.thumbs.forEach((thumb, i) => {
      if (i === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    // A focused zoom button inside the outgoing slide would be lost to <body> once the slide is inert:
    // hand focus to the same control in the incoming slide, or to the track.
    const focusedSlide = this.slides.find((slide) => slide.contains(document.activeElement));
    const target = this.slides[index];
    if (focusedSlide && focusedSlide !== target) {
      target.removeAttribute('inert');
      // Only a control that is actually shown (a started video hides its play button).
      const replacement =
        [...target.querySelectorAll('[data-pude-zoom], [data-pude-play], [data-pude-toggle], video[controls]')].find(
          (el) => el.getClientRects().length
        ) || this.track;
      replacement.focus({ preventScroll: true });
    }
    this.slides.forEach((slide, i) => slide.toggleAttribute('inert', i !== index));
    const atStart = index === 0;
    const atEnd = index === this.slides.length - 1;
    // Move focus off an arrow before it becomes disabled, so keyboard focus is never lost.
    if (atStart && this.prev && document.activeElement === this.prev) (this.next || this.track).focus();
    if (atEnd && this.next && document.activeElement === this.next) (this.prev || this.track).focus();
    if (this.prev) this.prev.disabled = atStart;
    if (this.next) this.next.disabled = atEnd;
  }

  #onIntersect = (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) this.#setIndex(this.slides.indexOf(entry.target));
    }
  };

  #onThumb = (event) => {
    const index = this.thumbs.indexOf(event.currentTarget);
    if (index > -1) this.go(index, true);
  };

  #onKey = (event) => {
    // Arrow keys inside a playing video belong to its native controls (seek, volume).
    if (event.target instanceof Element && event.target.closest('pude-video')) return;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.go(this.index + 1, true);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.go(this.index - 1, true);
    }
  };
}

/* ---------- <pude-bar> (mobile purchase bar) ---------- */

const CONSENT_IDS = ['shopify-pc__banner', 'shopify-pc__prefs'];

function consentOpen() {
  return CONSENT_IDS.some((id) => {
    const el = document.getElementById(id);
    if (!el) return false;
    return typeof el.checkVisibility === 'function' ? el.checkVisibility() : el.getClientRects().length > 0;
  });
}

class PudeBar extends HTMLElement {
  #offersInView = new Set();
  #observed = false;
  #abort;

  connectedCallback() {
    this.productId = this.dataset.productId;
    this.bar = this.querySelector('.pude-bar');
    this.button = this.querySelector('[data-pude-submit]');
    this.labelNode = this.button?.querySelector('[data-pude-label]');
    this.priceNode = this.button?.querySelector('[data-pude-price]');
    this.status = this.querySelector('[data-pude-status]');
    if (!this.bar || !this.button) return;

    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.button.addEventListener('click', this.#onClick, { signal });
    document.addEventListener(PACK_EVENT, this.#onPackChange, { signal });
    document.addEventListener(BUSY_EVENT, this.#onBusy, { signal });
    document.addEventListener(OFFER_EVENT, this.#onOfferConnected, { signal });
    // Phones and portrait tablets: up to the two-column hero (990px), as in pude.css.
    this.mobile = window.matchMedia('(max-width: 989px)');
    this.mobile.addEventListener('change', this.#update, { signal });

    this.#observeOffers();
    // The consent banner is added to <body> and later hidden with inline styles. Watch node additions
    // only to find it (no style reads), and its own attributes once it exists: other DOM changes in the
    // page must not trigger layout reads here.
    this.consentObserver?.disconnect();
    this.bannerObserver?.disconnect();
    this.bannerObserver = new MutationObserver(this.#scheduleUpdate);
    this.#watchedBanners = new Set();
    this.consentObserver = new MutationObserver(this.#watchBanners);
    this.consentObserver.observe(document.body, { childList: true, subtree: true });
    this.#watchBanners();
    this.#render();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.observer?.disconnect();
    this.fieldsetObserver?.disconnect();
    this.consentObserver?.disconnect();
    this.bannerObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #watchedBanners = new Set();
  #frame = 0;

  #watchBanners = () => {
    let changed = false;
    for (const id of CONSENT_IDS) {
      const el = document.getElementById(id);
      if (el && !this.#watchedBanners.has(el)) {
        this.#watchedBanners.add(el);
        this.bannerObserver.observe(el, { attributes: true, attributeFilter: ['style', 'class', 'hidden', 'open'] });
        changed = true;
      }
    }
    for (const el of [...this.#watchedBanners]) {
      if (!el.isConnected) {
        this.#watchedBanners.delete(el);
        changed = true;
      }
    }
    if (changed) this.#scheduleUpdate();
  };

  #scheduleUpdate = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #observeOffers() {
    this.observer?.disconnect();
    this.#offersInView.clear();
    // Watch the offers' add-to-cart buttons, not the whole offers: the bar stays until a real
    // "Læg i kurv" button is on screen, even when the top of a pack list peeks into view.
    const buttons = [
      ...document.querySelectorAll(`pude-offer[data-product-id="${this.productId}"] [data-pude-submit]`),
    ];
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#offersInView.add(entry.target);
          else this.#offersInView.delete(entry.target);
        }
        this.#observed = true;
        this.#update();
      },
      { threshold: 0.5 }
    );
    buttons.forEach((button) => this.observer.observe(button));
    // The pack lists of all offers count too (hero and final offer): the bar never covers a pack card.
    this.fieldsetObserver?.disconnect();
    const fieldsets = [
      ...document.querySelectorAll(`pude-offer[data-product-id="${this.productId}"] .pude-offer__fieldset`),
    ];
    if (fieldsets.length) {
      this.fieldsetObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) this.#offersInView.add(entry.target);
            else this.#offersInView.delete(entry.target);
          }
          this.#update();
        },
        // Hide as soon as the pack list clears the bar's band, so the bar never covers the first card.
        { threshold: 0, rootMargin: `0px 0px -${76}px 0px` }
      );
      fieldsets.forEach((fieldset) => this.fieldsetObserver.observe(fieldset));
    }
  }

  #heroOffer() {
    const offers = [...document.querySelectorAll(`pude-offer[data-product-id="${this.productId}"]`)];
    return offers.find((offer) => offer.dataset.context === 'hero') || offers[0];
  }

  #onOfferConnected = (event) => {
    if (event.detail.productId === this.productId) this.#observeOffers();
  };

  #update = () => {
    // Shown on phones whenever no pack selector is on screen (also before the hero offer is reached),
    // so the entry price and the add-to-cart action are always within thumb reach.
    const visible = this.mobile.matches && this.#observed && this.#offersInView.size === 0 && !consentOpen();
    if (visible === this.bar.hasAttribute('data-visible')) return;
    this.bar.toggleAttribute('data-visible', visible);
    if (visible) this.bar.removeAttribute('inert');
    else this.bar.setAttribute('inert', '');
  };

  #onPackChange = (event) => {
    if (event.detail.productId !== this.productId) return;
    this.#hideError();
    this.#render();
  };

  #onBusy = (event) => {
    this.button.toggleAttribute('data-locked', event.detail.busy);
  };

  #hideError() {
    clearStatus(this.status);
    this.status?.classList.add('pude-visually-hidden');
  }

  #render() {
    const variantId = getSelected(this.productId);
    const pack = variantId ? packData(this.productId, variantId) : null;
    if (!pack) return;
    if (this.labelNode && pack.cta && !this.button.hasAttribute('aria-busy')) this.labelNode.textContent = pack.cta;
    if (this.priceNode) this.priceNode.textContent = pack.price;
    if (pack.available) this.button.removeAttribute('aria-disabled');
    else this.button.setAttribute('aria-disabled', 'true');
  }

  #onClick = () => {
    const variantId = getSelected(this.productId);
    const pack = variantId ? packData(this.productId, variantId) : null;
    if (!pack || !pack.available) {
      const hero = this.#heroOffer();
      hero?.scrollIntoView({ block: 'start', behavior: reducedMotion() ? 'auto' : 'smooth' });
      hero?.querySelector('input[name="id"]:not(:disabled)')?.focus({ preventScroll: true });
      return;
    }
    this.#hideError();
    runAdd(this, variantId, this.button, this.status, () => this.status?.classList.remove('pude-visually-hidden'));
  };
}

/* ---------- <pude-dispatch> (order cut-off for same-day dispatch) ---------- */

/**
 * Shows "order within X, we ship today" from the real Copenhagen clock and the owner's cut-off hour.
 * After the cut-off, on weekends and on closed dates it names the next dispatch day instead.
 * Nothing resets or loops: the text always matches the actual next dispatch.
 */
class PudeDispatch extends HTMLElement {
  connectedCallback() {
    this.cutoff = Number(this.dataset.cutoff || 14);
    this.closed = new Set((this.dataset.closed || '').split(',').map((d) => d.trim()).filter(Boolean));
    this.textNode = this.querySelector('[data-pude-dispatch-text]');
    this.row = this.closest('[data-pude-dispatch-row]') || this;
    this.#render();
    window.clearInterval(this.timer);
    this.timer = window.setInterval(() => this.#render(), 30000);
    document.removeEventListener('shopify:cart:lines-update', this.#onCartUpdate);
    document.addEventListener('shopify:cart:lines-update', this.#onCartUpdate);
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
    document.removeEventListener('shopify:cart:lines-update', this.#onCartUpdate);
  }

  /** The drawer morphs its markup after cart changes (restoring `hidden` and the empty text): render again. */
  #onCartUpdate = (event) => {
    const settle = event.promise ? event.promise.catch(() => {}) : Promise.resolve();
    settle.then(() => window.setTimeout(() => this.isConnected && this.#render(), 150));
  };

  static #formatter = null;

  /** Copenhagen calendar parts for a given instant (one formatter, created on first use). */
  static #parts(date) {
    PudeDispatch.#formatter ??= new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Copenhagen',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
      weekday: 'short',
    });
    const parts = PudeDispatch.#formatter.formatToParts(date);
    const get = (type) => parts.find((p) => p.type === type)?.value;
    const weekday = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(get('weekday'));
    return {
      iso: `${get('year')}-${get('month')}-${get('day')}`,
      weekday,
      minutes: Number(get('hour')) * 60 + Number(get('minute')),
    };
  }

  #isDispatchDay(parts) {
    return parts.weekday >= 0 && parts.weekday <= 4 && !this.closed.has(parts.iso);
  }

  #render() {
    if (!this.textNode) return;
    const now = new Date();
    const today = PudeDispatch.#parts(now);
    const cutoffMinutes = this.cutoff * 60;
    let text = '';

    if (this.#isDispatchDay(today) && today.minutes < cutoffMinutes) {
      const left = cutoffMinutes - today.minutes;
      const hours = Math.floor(left / 60);
      const minutes = left % 60;
      const time =
        hours > 0
          ? `${hours} ${this.dataset.hoursUnit} ${minutes} ${this.dataset.minutesUnit}`
          : `${minutes} ${this.dataset.minutesUnit}`;
      text = (this.dataset.today || '').replace('{{ time }}', time);
    } else {
      const weekdays = (this.dataset.weekdays || '').split('|');
      for (let offset = 1; offset <= 14; offset += 1) {
        const next = PudeDispatch.#parts(new Date(now.getTime() + offset * 86400000));
        if (!this.#isDispatchDay(next)) continue;
        const day = offset === 1 ? this.dataset.tomorrow : weekdays[next.weekday];
        text = (this.dataset.later || '').replace('{{ day }}', day || '');
        break;
      }
    }

    if (this.textNode.textContent !== text) this.textNode.textContent = text;
    if (this.row.hidden !== !text) this.row.hidden = !text;
  }
}

/* ---------- Cart drawer suggestions (pack upgrade, accessory) ---------- */

/**
 * Swaps the single pack line for the next pack, or adds an accessory, then lets Horizon re-render
 * the drawer from the returned section HTML (same contract as the add-to-cart flow above).
 */
async function updateCartFromDrawer(button) {
  if (busy) return;
  const swapFrom = button.dataset.pudeSwapFrom;
  const variantId = button.dataset.pudeVariant;
  if (!variantId) return;

  setBusy(true);
  button.setAttribute('aria-busy', 'true');
  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: swapFrom ? 'update' : 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const sections = cartSectionIds().join(',');
    const request = swapFrom
      ? { url: cartUpdateUrl(), body: { updates: { [swapFrom]: 0, [variantId]: 1 } } }
      : { url: cartAddUrl(), body: { items: [{ id: Number(variantId), quantity: 1 }] } };
    const response = await fetch(request.url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ ...request.body, sections, sections_url: window.location.pathname }),
    });
    const data = await response.json();
    const cart = await fetchCartSafely();
    const failed = !response.ok || Boolean(data.status);
    if (failed) {
      button.dispatchEvent(new CartErrorEvent({ error: data.description || data.message || strings.error, code: 'INVALID' }));
    }
    resolveCartEvent(deferred, cart, {
      itemCount: 1,
      source: 'pude-cart-suggestion',
      sections: failed ? undefined : data.sections,
      didError: failed,
    });
  } catch (error) {
    deferred.reject(error);
  } finally {
    button.removeAttribute('aria-busy');
    setBusy(false);
  }
}

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-pude-cart-action]') : null;
  if (!button) return;
  event.preventDefault();
  updateCartFromDrawer(button);
});

/* ---------- "Vælg Duo-pakken" (sections/pude-duo) ---------- */

/**
 * Selects a pack everywhere (offers, mobile bar) and brings the first offer on screen with that pack
 * checked and its button reading "Læg 2 puder i kurven · 882,01 kr". Without that offer on the page,
 * the link opens the product page with ?variant= instead.
 */
document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('[data-pude-choose]') : null;
  if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
  const { productId, variantId } = link.dataset;
  const offers = [...document.querySelectorAll(`pude-offer[data-product-id="${productId}"]`)];
  const offer = offers.find((el) => el.dataset.context === 'hero') || offers[0];
  const input = offer?.querySelector(`input[name="id"][value="${CSS.escape(variantId)}"]:not(:disabled)`);
  if (!input) return;
  event.preventDefault();
  setSelected(productId, variantId, link, true);
  const target = offer.querySelector('.pude-offer__fieldset') || offer;
  target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
  input.focus({ preventScroll: true });
});

/* ---------- Links to folded answers (#maal, #levering, ...) open them ---------- */

/** Opens the <details> that an in-page address targets (the item itself or anything inside it). */
function openFoldedTarget(hash) {
  if (!hash || hash.length < 2) return;
  let target = null;
  try {
    target = document.getElementById(decodeURIComponent(hash.slice(1)));
  } catch {
    return;
  }
  const details = target?.closest('details');
  if (details && !details.open) details.open = true;
}

// Capture phase: the item is open before the browser (or the header handler below) scrolls to it.
document.addEventListener(
  'click',
  (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href*="#"]') : null;
    if (!link) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin === window.location.origin && url.pathname === window.location.pathname) openFoldedTarget(url.hash);
  },
  true
);
window.addEventListener('hashchange', () => openFoldedTarget(window.location.hash));
if (window.location.hash) openFoldedTarget(window.location.hash);

/* ---------- Header in-page links ---------- */

/**
 * The header menu links to "/#brug", "/#detaljer" and "/#faq" so they work from every page. On a landing page
 * that has the target section (homepage or product page), scroll there instead of loading "/", which
 * keeps the chosen pack, ?variant= and campaign parameters.
 */
document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('#header-component a[href*="#"]') : null;
  if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
  const url = new URL(link.href, window.location.href);
  if (url.origin !== window.location.origin || !url.hash) return;
  const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
  if (!target) return;
  event.preventDefault();
  openFoldedTarget(url.hash);
  // From the phone menu drawer: close it first (it locks page scroll), then jump.
  const drawer = link.closest('header-drawer');
  if (drawer?.isOpen) drawer.close();
  requestAnimationFrame(() => target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' }));
  window.history.pushState(window.history.state, '', `${window.location.pathname}${window.location.search}${url.hash}`);
});

/* ---------- Accordions: keep an opened answer visible (pude-accordion-reveal) ---------- */

// `toggle` does not bubble: listen in the capture phase. An answer opened near the bottom of the
// screen would otherwise sit under the sticky buy bar.
document.addEventListener(
  'toggle',
  (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open || !details.classList.contains('pude-accordion')) return;
    requestAnimationFrame(() => {
      const bar = document.querySelector('.pude-bar[data-visible]');
      const limit = window.innerHeight - (bar ? bar.getBoundingClientRect().height : 0);
      if (details.getBoundingClientRect().bottom > limit) {
        details.scrollIntoView({ block: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' });
      }
    });
  },
  true
);

/* ---------- <pude-video> (plays by itself when on screen, or on click; pauses off screen) ---------- */

/**
 * Poster first, nothing downloaded until the visitor presses play (preload="none").
 * Never autoplays; pauses when scrolled or swiped out of view; the play button returns at the end.
 */
class PudeVideo extends HTMLElement {
  /** The one video allowed to play: starting another pauses it (one active video on the page). */
  static active = null;
  #abort;
  #visible = false;
  #internalPause = false;

  connectedCallback() {
    this.video = this.querySelector('video');
    this.button = this.querySelector('[data-pude-play]');
    this.toggle = this.querySelector('[data-pude-toggle]');
    if (!this.video || !this.button) return;
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    // Muted, inline, looping clips may start by themselves; not with "reduce motion" or "save data".
    const connection = navigator.connection;
    const slow = connection?.saveData || /(^|-)2g$|^3g$/.test(connection?.effectiveType || '');
    const auto = this.hasAttribute('data-autoplay') && !reducedMotion() && !slow;
    this.toggleAttribute('data-auto', auto);
    this.button.addEventListener('click', this.#play, { signal });
    this.toggle?.addEventListener('click', this.#toggleByUser, { signal });
    this.video.addEventListener('ended', this.#reset, { signal });
    // The pause/play button always shows the real state (it may be paused off screen, not by the visitor).
    this.video.addEventListener(
      'pause',
      () => {
        this.toggleAttribute('data-playing', false);
        this.toggle?.setAttribute('aria-label', this.toggle.dataset.labelPlay);
        // A pause from the native controls is the visitor's: no autoplay again when it comes back on screen.
        if (!this.#internalPause && this.video.controls) this.toggleAttribute('data-user-paused', true);
        this.#internalPause = false;
      },
      { signal }
    );
    this.video.addEventListener(
      'play',
      () => {
        this.toggleAttribute('data-playing', true);
        this.toggleAttribute('data-user-paused', false);
        this.toggle?.setAttribute('aria-label', this.toggle.dataset.labelPause);
        const other = PudeVideo.active;
        if (other && other !== this && other.isConnected && !other.video.paused) other.pauseQuietly();
        PudeVideo.active = this;
      },
      { signal }
    );
    // Hide the poster only once a frame is ready, so there is never a blank flash.
    this.video.addEventListener(
      'playing',
      () => {
        const hadFocus = this.button.contains(document.activeElement);
        this.#showPoster(false);
        // Autoplay hides the play button: keep keyboard focus on the control that replaces it.
        if (hadFocus && this.hasAttribute('data-auto')) (this.video.controls ? this.video : this.toggle || this.video).focus({ preventScroll: true });
      },
      { signal }
    );
    this.observer?.disconnect();
    // Decide by visible ratio, with a small non-zero first threshold: a video started by the visitor
    // while less than half visible still pauses once it leaves the screen (or its gallery slide).
    const min = auto ? 0.5 : 0.25;
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const enough = entry.isIntersecting && entry.intersectionRatio >= min;
          const gone = !entry.isIntersecting || entry.intersectionRatio < 0.01;
          this.#visible = enough;
          if (gone || (!enough && !this.hasAttribute('data-user-started'))) {
            if (!this.video.paused) {
              this.pauseQuietly();
              // Swipe row: a card that crossed the threshold while this one played gets no new callback.
              setTimeout(() => PudeVideo.#resumeVisible(this));
            }
            if (gone) this.toggleAttribute('data-user-started', false);
          } else if (enough && this.hasAttribute('data-auto') && !this.hasAttribute('data-user-paused')) {
            // Decided after all callbacks of this frame: never take over from a playing video, and when two
            // cards reach the screen together the first one in reading order starts.
            setTimeout(() => PudeVideo.#resumeVisible(null));
          }
        }
      },
      { threshold: [0.01, min] }
    );
    this.observer.observe(this);
  }

  /** Starts the first muted clip that is on screen and allowed to autoplay, unless a video is playing. */
  static #resumeVisible(from) {
    const active = PudeVideo.active;
    if (active && active !== from && active.isConnected && !active.video.paused) return;
    for (const el of document.querySelectorAll('pude-video[data-auto]:not([data-user-paused])')) {
      if (el !== from && el instanceof PudeVideo && el.#visible && el.video.paused) {
        el.#autoplay();
        break;
      }
    }
  }

  #autoplay = async () => {
    // Claim the single active slot before play(): a second card reaching the screen in the same frame
    // then sees a playing video and waits (no second download, no race).
    const other = PudeVideo.active;
    if (other && other !== this && other.isConnected) other.pauseQuietly();
    PudeVideo.active = this;
    this.video.muted = true;
    if (this.video.preload === 'none') this.video.preload = 'auto';
    try {
      await this.video.play();
    } catch (error) {
      // AbortError: our own pause() interrupted a play still loading, autoplay stays on. Any other
      // rejection (e.g. NotAllowedError in low-power mode): fall back to the play button.
      if (error?.name !== 'AbortError') this.toggleAttribute('data-auto', false);
    }
  };

  /** Pause from code (off screen, another video started): not a visitor's pause. */
  pauseQuietly() {
    if (this.video.paused) return;
    this.#internalPause = true;
    this.video.pause();
  }

  #toggleByUser = () => {
    const pausing = !this.video.paused;
    if (pausing) {
      this.toggleAttribute('data-user-paused', true);
      this.toggleAttribute('data-user-started', false);
      this.pauseQuietly();
    } else {
      this.#startByUser();
    }
  };

  /** A play asked by the visitor: kept while partly on screen, with the native controls (full screen). */
  #startByUser() {
    this.toggleAttribute('data-user-paused', false);
    this.toggleAttribute('data-user-started', true);
    this.video.controls = true;
    this.toggleAttribute('data-controls', true);
    if (this.button.contains(document.activeElement) || this.toggle?.contains(document.activeElement)) {
      this.video.focus({ preventScroll: true });
    }
    this.#autoplay();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.observer?.disconnect();
  }

  #play = async () => {
    if (this.hasAttribute('data-auto')) {
      this.#startByUser();
      return;
    }
    this.video.controls = true;
    // Keep keyboard focus: move it to the video before the play button disappears.
    if (this.button.contains(document.activeElement)) this.video.focus({ preventScroll: true });
    this.button.hidden = true;
    this.#showPoster(false);
    try {
      await this.video.play();
    } catch {
      this.#reset();
    }
  };

  #reset = () => {
    const hadFocus = this.contains(document.activeElement);
    this.video.controls = false;
    this.button.hidden = false;
    this.toggleAttribute('data-playing', false);
    if (hadFocus) this.button.focus({ preventScroll: true });
  };

  #showPoster(show) {
    // A host attribute (CSS hides the poster) restyles far less than toggling `hidden` on the image.
    if (this.hasAttribute('data-started') === !show) return;
    this.toggleAttribute('data-started', !show);
  }
}

/* ---------- <pude-carousel> (reviews: one card at a time on phones, grid on desktop) ---------- */

class PudeCarousel extends HTMLElement {
  #abort;
  #frame = 0;

  connectedCallback() {
    this.track = this.querySelector('[data-pude-track]');
    this.slides = [...this.querySelectorAll('[data-pude-slide]')];
    this.prev = this.querySelector('[data-pude-prev]');
    this.next = this.querySelector('[data-pude-next]');
    this.counter = this.querySelector('[data-pude-count]');
    this.live = this.querySelector('[data-pude-live]');
    if (!this.track || this.slides.length < 2) return;

    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.prev?.addEventListener('click', () => this.go(this.index - 1), { signal });
    this.next?.addEventListener('click', () => this.go(this.index + 1), { signal });
    // The current card is derived from the scroll position (not from intersections), so partly
    // visible neighbours never shift the counter, at any width or after a resize.
    this.track.addEventListener('scroll', this.#schedule, { signal, passive: true });
    window.addEventListener('resize', this.#schedule, { signal, passive: true });
    this.#setIndex(this.#currentFromScroll());
  }

  disconnectedCallback() {
    this.#abort?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #offset(slide) {
    const padding = parseFloat(getComputedStyle(this.track).scrollPaddingInlineStart) || 0;
    return slide.offsetLeft - this.track.offsetLeft - padding;
  }

  #currentFromScroll() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    if (scrollLeft >= scrollWidth - clientWidth - 1 && scrollWidth > clientWidth) return this.slides.length - 1;
    let best = 0;
    let bestDistance = Infinity;
    this.slides.forEach((slide, i) => {
      const distance = Math.abs(this.#offset(slide) - scrollLeft);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    });
    return best;
  }

  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#setIndex(this.#currentFromScroll()));
  };

  go(index) {
    const target = Math.max(0, Math.min(this.slides.length - 1, index));
    const slide = this.slides[target];
    this.track.scrollTo({ left: this.#offset(slide), behavior: reducedMotion() ? 'auto' : 'smooth' });
    this.#setIndex(target);
    if (this.live) this.live.textContent = slide.getAttribute('aria-label') || '';
  }

  #setIndex(index) {
    if (index === this.index) return;
    this.index = index;
    const text = `${index + 1} / ${this.slides.length}`;
    if (this.counter && this.counter.textContent !== text) this.counter.textContent = text;
    const atStart = index === 0;
    const atEnd = index === this.slides.length - 1;
    if (atStart && this.prev && document.activeElement === this.prev) this.next?.focus();
    if (atEnd && this.next && document.activeElement === this.next) this.prev?.focus();
    if (this.prev && this.prev.disabled !== atStart) this.prev.disabled = atStart;
    if (this.next && this.next.disabled !== atEnd) this.next.disabled = atEnd;
  }
}

/* ---------- Tap-to-zoom lightbox ([data-pude-zoom]) ---------- */

/**
 * Opens the large version of an image in a native modal <dialog> (focus trap, Escape, focus return).
 * Tapping the image toggles a 2x view that can be panned; the backdrop or "Luk" closes it.
 */
let lightbox = null;

function getLightbox() {
  if (lightbox) return lightbox;
  const dialog = document.createElement('dialog');
  dialog.className = 'pude-lightbox';
  dialog.innerHTML = `
    <div class="pude-lightbox__bar">
      <button type="button" class="pude-lightbox__button" data-pude-lightbox-zoom aria-pressed="false"></button>
      <button type="button" class="pude-lightbox__button" data-pude-lightbox-close></button>
    </div>
    <div class="pude-lightbox__scroller"><img class="pude-lightbox__image" alt="" decoding="async"></div>
    <p class="pude-lightbox__hint"></p>`;
  const close = dialog.querySelector('[data-pude-lightbox-close]');
  const zoom = dialog.querySelector('[data-pude-lightbox-zoom]');
  close.textContent = strings.close || 'Luk';
  zoom.textContent = strings.zoom_in || 'Zoom';
  dialog.querySelector('.pude-lightbox__hint').textContent = strings.zoom_hint || '';
  const image = dialog.querySelector('img');
  const scroller = dialog.querySelector('.pude-lightbox__scroller');

  const hint = dialog.querySelector('.pude-lightbox__hint');
  const toggleZoom = () => {
    const zoomed = dialog.classList.toggle('is-zoomed');
    zoom.setAttribute('aria-pressed', String(zoomed));
    zoom.textContent = zoomed ? strings.zoom_out || 'Zoom' : strings.zoom_in || 'Zoom';
    hint.textContent = zoomed ? strings.zoom_hint_out || strings.zoom_hint || '' : strings.zoom_hint || '';
    // The full-size file (up to 2880 px) is only needed once zoomed in.
    const large = dialog.dataset.target;
    if (zoomed && large && image.src !== large) {
      const preload = new Image();
      preload.onload = () => {
        if (dialog.open && dialog.dataset.target === large) image.src = large;
      };
      preload.src = large;
    }
    if (zoomed) {
      scroller.scrollLeft = (scroller.scrollWidth - scroller.clientWidth) / 2;
      scroller.scrollTop = (scroller.scrollHeight - scroller.clientHeight) / 2;
    }
  };

  close.addEventListener('click', () => dialog.close());
  zoom.addEventListener('click', toggleZoom);
  image.addEventListener('click', toggleZoom);
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog || event.target === scroller) dialog.close();
  });
  // Horizon's open cart drawer (non-modal on desktop) also handles Escape on document and calls
  // preventDefault; keep Escape inside the lightbox so it closes the lightbox, not the drawer behind it.
  dialog.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') event.stopPropagation();
  });
  dialog.addEventListener('close', () => {
    dialog.classList.remove('is-zoomed');
    zoom.setAttribute('aria-pressed', 'false');
    zoom.textContent = strings.zoom_in || 'Zoom';
    hint.textContent = strings.zoom_hint || '';
    delete dialog.dataset.target;
    document.documentElement.classList.remove('pude-lightbox-open');
  });
  document.body.append(dialog);
  lightbox = dialog;
  return dialog;
}

document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('[data-pude-zoom]') : null;
  if (!trigger || !trigger.dataset.pudeZoomSrc) return;
  event.preventDefault();
  const dialog = getLightbox();
  const image = dialog.querySelector('img');
  const large = new URL(trigger.dataset.pudeZoomSrc, window.location.href).href;
  const label = trigger.dataset.pudeZoomAlt || '';
  dialog.dataset.target = large;
  dialog.setAttribute('aria-label', label || strings.close || '');
  // Show the already-loaded inline image at once (sharp enough at screen size); the large file is
  // fetched only when the visitor zooms in (see toggleZoom).
  const inline = trigger.querySelector('img');
  image.src = inline?.currentSrc || large;
  image.alt = label;
  document.documentElement.classList.add('pude-lightbox-open');
  dialog.showModal();
});

/* ---------- Cart drawer after browser Back (e.g. returning from checkout) ---------- */

/**
 * Back/forward navigation can show the page HTML cached at first load, so the drawer may say the cart
 * is empty while it is not. Re-render the drawer section from Shopify whenever the page comes from history.
 */
async function refreshCartDrawerFromServer() {
  const ids = cartSectionIds();
  if (!ids.length) return;
  try {
    const [response, cart] = await Promise.all([
      fetch(`${window.location.pathname}?sections=${ids.join(',')}`, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      }),
      fetch(`${cartUrl()}.js`, { cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(
        (r) => (r.ok ? r.json() : null)
      ),
    ]);
    if (!response.ok || !cart) return;
    const sections = await response.json();
    // Horizon's listeners attach when their modules define the elements: wait so the event is not missed.
    await Promise.all(['cart-items-component', 'cart-icon'].map((tag) => customElements.whenDefined(tag)));
    // Same contract as an add: Horizon re-renders the drawer from `sections` and the header bubble from `cart`.
    const deferred = CartLinesUpdateEvent.createPromise();
    document.body.dispatchEvent(
      new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise })
    );
    resolveCartEvent(deferred, cart, { itemCount: cart.item_count, source: 'pude-history-restore', sections, didError: false });
  } catch (error) {
    console.warn('[pude] cart drawer refresh failed', error);
  }
}

const navigation = performance.getEntriesByType('navigation')[0];
if (navigation?.type === 'back_forward') refreshCartDrawerFromServer();
window.addEventListener('pageshow', (event) => {
  if (event.persisted) refreshCartDrawerFromServer();
});

if (!customElements.get('pude-video')) customElements.define('pude-video', PudeVideo);
if (!customElements.get('pude-carousel')) customElements.define('pude-carousel', PudeCarousel);
if (!customElements.get('pude-offer')) customElements.define('pude-offer', PudeOffer);
if (!customElements.get('pude-gallery')) customElements.define('pude-gallery', PudeGallery);
if (!customElements.get('pude-bar')) customElements.define('pude-bar', PudeBar);
if (!customElements.get('pude-dispatch')) customElements.define('pude-dispatch', PudeDispatch);
